import React, { useState } from 'react';
import config from '../config/environment';

const JoinRoom = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    roomId: '',
    playerName: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const checkRoom = async () => {
    if (!formData.roomId) {
      setError('Room ID is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.getApiUrl()}/api/room/${formData.roomId}/info`);
      const result = await response.json();

      if (result.success) {
        setRoomInfo(result.roomData);
        setError('');
      } else {
        setError(result.error || 'Room not found');
        setRoomInfo(null);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setRoomInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async () => {
    if (!formData.playerName) {
      setError('Player name is required');
      return;
    }

    if (roomInfo && roomInfo.isPrivate && !formData.password) {
      setError('Password is required for private rooms');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.getApiUrl()}/api/room/${formData.roomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: formData.playerName,
          password: formData.password
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Navigate to game
        onNavigate('game', {
          roomId: formData.roomId,
          playerName: formData.playerName,
          password: formData.password
        });
      } else {
        setError(result.error || 'Failed to join room');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getGameStatus = (status) => {
    switch (status) {
      case 'waiting': return 'Waiting for players';
      case 'playing': return 'Game in progress';
      case 'finished': return 'Game finished';
      default: return 'Unknown';
    }
  };

  return (
    <div className="container">
      <div className="card">
        <button className="back-button" onClick={() => onNavigate('home')}>
          ← Back to Home
        </button>
        
        <h2>Join Game Room</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-container">
          <div className="form-group">
            <label htmlFor="roomId">Room ID:</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                id="roomId"
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                placeholder="Enter room ID"
                maxLength="10"
                required
              />
              <button 
                type="button" 
                onClick={checkRoom}
                disabled={loading}
                className="secondary-button"
              >
                {loading ? 'Checking...' : 'Check Room'}
              </button>
            </div>
          </div>

          {roomInfo && (
            <div className="room-info card" style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa' }}>
              <h3>Room Information</h3>
              <p><strong>Room ID:</strong> {roomInfo.roomId}</p>
              <p><strong>Players:</strong> {roomInfo.playersCount}/{roomInfo.maxPlayers}</p>
              <p><strong>Status:</strong> {getGameStatus(roomInfo.status)}</p>
              <p><strong>Private:</strong> {roomInfo.isPrivate ? 'Yes' : 'No'}</p>
              
              {roomInfo.playersCount > 0 && (
                <div>
                  <strong>Players in room:</strong>
                  <ul>
                    {Object.values(roomInfo.players).map((player, index) => (
                      <li key={index}>{player.name} ({player.color})</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="playerName">Your Name:</label>
            <input
              type="text"
              id="playerName"
              name="playerName"
              value={formData.playerName}
              onChange={handleInputChange}
              placeholder="Enter your name"
              maxLength="20"
              required
            />
          </div>

          {roomInfo && roomInfo.isPrivate && (
            <div className="form-group">
              <label htmlFor="password">Room Password:</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter room password"
                maxLength="20"
                required
              />
            </div>
          )}

          <button
            onClick={joinRoom}
            disabled={loading || !roomInfo}
            className="primary-button"
            style={{ width: '100%', marginTop: '20px' }}
          >
            {loading ? 'Joining Room...' : 'Join Room'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
